import { forEach } from 'lodash';
import { addPointerListener } from 'uScope/js/lib/utility/pointerApi';
import { Point } from '../../../lib/objects/Point';

/**
 * The "Drawing surface" is a construct that manages the relationship between a canvas and the things that can be drawn on it. It's
 * expected to be used in conjunction with a "renderer" which will periodically call "draw()" on the surface.
 * This also provides an interface to allow consumers to specify mouse handlers. Only one set of mouse handlers can be active at a
 * time. This makes it simpler to manage conditions where we want to change the behavior of what happens when the user interacts
 * with the canvas. For instance, if we want to pan the background, we'd pass in a set of "pan handlers". If we wanted to zoom, we'd
 * pass in "zoom handlers". If we wanted to draw, well, you get the idea.
 */
export class DrawingSurface {
  constructor (canvas, offsetElement) {
    offsetElement = offsetElement || canvas;
    const self = this;

    // Store the base position of the drawing surface.
    const position = new Point();

    // Store a reference to the canvas's drawing context.
    const ctx = canvas.getContext('2d');

    // Store information about the viewport's style (useful for calculations during the draw() cycle).
    const style = calculateStyleOffsets(offsetElement);

    // the collection of things to be drawn
    // const shapes = [];

    // Determines whether the canvas and the shapes on it need to be redrawn.
    this.valid = false;

    /**
         * Setup event listeners.
         */

    // Stores mouse handlers defined by the consumer.
    let activeMouseHandlers = {};
    const internalMouseHandlers = {
      selectStart: function (e) {
        e.preventDefault();
        return false;
      }
    };
    // Prevents a problem where double clicking causes text to get selected on the canvas
    canvas.addEventListener('selectstart', internalMouseHandlers.selectStart, false);

    // Add "handy" mouse handler wrapper function
    ['dblclick', 'mousedown', 'mouseup', 'mousemove'].forEach(action => {
      internalMouseHandlers[action] = function (e) {
        if (!activeMouseHandlers[action]) {
          return;
        }

        const mouse = self.getPointerPosition(e);
        const dragOffset = new Point(
          mouse.x - self.position.x,
          mouse.y - self.position.y
        );

        activeMouseHandlers[action](mouse, dragOffset, e);

        e.preventDefault();
        return false;
      };
      addPointerListener(canvas, action, internalMouseHandlers[action], true);
    });

    /**
         * Configure public and private properties.
         */

    Object.defineProperties(this, {
      // Public, read-only properties
      ctx: {
        enumerable: true,
        value: ctx
      },
      width: {
        enumerable: true,
        get () { return canvas.width; }
      },
      height: {
        enumerable: true,
        get () { return canvas.height; }
      },
      position: {
        enumerable: true,
        value: position
      },

      // Private members
      _canvas: {
        value: canvas
      },
      _shapes: {
        value: []
      },
      _style: {
        value: style
      },
      _actions: {
        value: internalMouseHandlers
      },
      _handlers: {
        get () { return activeMouseHandlers; },
        set (value) { activeMouseHandlers = value; }
      }
    });
  }

  /**
     * Adds a shape that needs to be rendered in the draw cycle and dirty's the canvas ensuring that the shape will
     * be included in the next refresh.
     */
  addShape (shape) {
    this._shapes.push(shape);
    this.valid = false;
  }

  removeAllShapes () {
    this._shapes.splice(0);
  }

  /**
     * Removes everything from the canvas.
     */
  clear () {
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  /**
     * Called by the renderer. Calls draw() on all children, but only if the canvas was invalidated.
     */
  draw () {
    // Only redraw when needed.
    if (!this.valid) {
      this.clear();

      // Draw all shapes (note that internally shapes should not render if they're off the canvas).
      for (let i = 0; i < this._shapes.length; i++) {
        this._shapes[i].draw(this);
      }

      this.valid = true;
    }
  }

  /**
     * Creates an object with x and y defined, set to the mouse position relative to the state's canvas
     * If you wanna be super-correct this can be tricky, we have to worry about padding and borders
     */
  getPointerPosition (e) {
    let element = this._canvas;
    let offsetX = 0;
    let offsetY = 0;
    let mx;
    let my;

    // Compute the total offset
    if (element.offsetParent !== undefined) {
      do {
        offsetX += element.offsetLeft;
        offsetY += element.offsetTop;
      } while ((element = element.offsetParent));
    }

    // Add padding and border style widths to offset
    // Also add the <html> offsets in case there's a position:fixed bar
    offsetX += this._style.padding.left + this._style.border.left + this._style.offset.left;
    offsetY += this._style.padding.top + this._style.border.top + this._style.offset.top;

    mx = e.pageX - offsetX;
    my = e.pageY - offsetY;

    return new Point(mx, my);
  }

  /**
     * Sets mouse handlers.
     * @param handlers - An object containing one or more of mousemove, mouseup, and mousedown events.
     */
  setMouseEventHandlers (handlers) {
    this._handlers = handlers;
  }

  /**
     * Removes all mouse handlers. Call when mouse events are no longer necessary.
     */
  clearMouseHandlers () {
    forEach(this._actions, (action, name) => {
      this._canvas.removeEventListener(name, action);
    });
    this.setMouseEventHandlers({});
  }
  /**
     * Memory management. Please call when the drawing surface goes out of scope.
     */
  destroy () {
    this.clearMouseHandlers();
  }
}

function calculateStyleOffsets (canvas, style = {
  offset: {},
  padding: {},
  border: {}
}) {
  // This complicates things a little but but fixes mouse co-ordinate problems when there's a border or
  // padding. See getPointerPosition for more detail.
  if (document.defaultView && document.defaultView.getComputedStyle) {
    style.padding.left = parseInt(document.defaultView.getComputedStyle(canvas, null).paddingLeft, 10) || 0;
    style.padding.top = parseInt(document.defaultView.getComputedStyle(canvas, null).paddingTop, 10) || 0;
    style.border.left = parseInt(document.defaultView.getComputedStyle(canvas, null).borderLeftWidth, 10) || 0;
    style.border.top = parseInt(document.defaultView.getComputedStyle(canvas, null).borderTopWidth, 10) || 0;
  }

  // Some pages have fixed-position bars at the top or left of the page; they will mess up mouse coordinates
  // and this fixes that.
  const htmlEl = document.body.parentNode;
  style.offset.top = htmlEl.offsetTop;
  style.offset.left = htmlEl.offsetLeft;

  return style;
}
